import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import { supabase } from './lib/supabase';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Sidebar from './components/Sidebar';
import AIPanel from './components/AIPanel';

// Pages (lazy loaded)
const Home = React.lazy(() => import('./pages/Home'));
const Leads = React.lazy(() => import('./pages/Leads'));
const Opportunities = React.lazy(() => import('./pages/Opportunities'));
const Clients = React.lazy(() => import('./pages/Clients'));
const Activities = React.lazy(() => import('./pages/Activities'));
const Contacts = React.lazy(() => import('./pages/Contacts'));
const KPIs = React.lazy(() => import('./pages/KPIs'));
const Tools = React.lazy(() => import('./pages/Tools'));
const Settings = React.lazy(() => import('./pages/Settings'));

const PAGE_MAP = {
  home: Home,
  leads: Leads,
  opportunities: Opportunities,
  clients: Clients,
  activities: Activities,
  contacts: Contacts,
  kpis: KPIs,
  tools: Tools,
  settings: Settings,
};

function CRMApp() {
  const { user, profile, loading } = useAuth();
  const [page, setPage] = useState('home');
  const [data, setData] = useState({
    leads: [], opportunities: [], clients: [],
    contacts: [], activities: [], notes: {}
  });
  const [dataLoading, setDataLoading] = useState(true);

  const loadAllData = useCallback(async () => {
    if (!user) return;
    setDataLoading(true);
    try {
      const [leads, opps, clients, contacts, activities] = await Promise.all([
        supabase.from('leads').select('*').eq('archived', false).order('created_at', { ascending: false }),
        supabase.from('opportunities').select('*').order('value', { ascending: false }),
        supabase.from('clients').select('*').eq('archived', false).order('contract_value', { ascending: false }),
        supabase.from('contacts').select('*').eq('archived', false).order('name'),
        supabase.from('activities').select('*').order('date', { ascending: false }).limit(200),
      ]);
      setData({
        leads: leads.data || [],
        opportunities: opps.data || [],
        clients: clients.data || [],
        contacts: contacts.data || [],
        activities: activities.data || [],
        notes: {},
      });
    } catch (err) {
      console.error('Data load error:', err);
    }
    setDataLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) loadAllData();
  }, [user, loadAllData]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--navy)', color: '#fff', fontFamily: 'Poppins', fontSize: 14 }}>
        Loading...
      </div>
    );
  }

  // Handle password reset route — must render before auth check
  if (window.location.pathname === '/reset-password') return <ResetPassword />;

  if (!user) return <Login />;

  const PageComponent = PAGE_MAP[page] || Home;

  // Badges
  const badges = {
    leads: data.leads.filter(l => !l.next_step).length || 0,
    opportunities: data.opportunities.filter(o => o.stage !== 'Closed Won' && o.stage !== 'Closed Lost' && !o.archived && !o.next_step).length || 0,
  };

  return (
    <div className="app-shell">
      <Sidebar page={page} onNavigate={setPage} badges={badges} data={data} profile={profile} />

      <div className="main-content">
        <React.Suspense fallback={
          <div style={{ padding: 40, color: 'var(--gray3)', fontFamily: 'Poppins' }}>Loading page...</div>
        }>
          <PageComponent
            data={data}
            profile={profile}
            onNavigate={setPage}
            onDataChange={loadAllData}
          />
        </React.Suspense>
      </div>

      <AIPanel context={data} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <CRMApp />
      </ToastProvider>
    </AuthProvider>
  );
}
